import React, { useEffect } from "react";
import DateRangePicker from "../../../../components/dateRangePicker";
import DatePicker from "../../../../components/datePicker/DatePicker";
import GuestSelector from "../../../../components/guestSelector";
import Icon from "../../../../components/icon";
import Modal from "../modal";
import provisionalModuleDataBasedOnLanguage from "../../searchList/provisionalModuleDataBasedOnLanguage";
import translations from "../../supplierPage/provisionalModuleDataBasedOnLanguage";
import { getLanguageField } from "../../../../utils/language";

import { addDays } from "../../../../utils/datefns";

/**
 * @typedef {function} OnSearch
 * @param {Date} arrivalDate - The start date of the search
 * @param {Date} departureDate - The end date of the search
 * @param {Array} guests - An array of guests for the search
 */

/**
 * A modal component for searching lodgings with a date range picker and a guest selector.
 *
 * @param {Date} [arrivalDate] - The current selected start date of the date range. Defaults to null.
 * @param {Date} [departureDate] - The current selected end date of the date range. Defaults to null.
 * @param {Array} [guests] - The current selected list of guests. Defaults to an empty array.
 * @param {boolean} modal - The state to control when the modal is opened or closed.
 * @param {function} closeFn - The function to close the modal.
 * @param {function} onSearch - The function called when a search is made. Defaults to function which returns null.
 * @returns {JSX.Element} The SearchModal component.
 */
const SearchModal = ({
  arrivalDate = null,
  departureDate = null,
  guests = [],
  open,
  closeFn,
  onSearch = () => null,
  type = "lodging",
  selectedLanguage,
  rangeInterval = [new Date(), new Date().setFullYear(new Date().getFullYear() + 1)],
  minStay = null,
  maxStay = null,
  staticArrivalDateForActivities = null,
  onMonthChange,
  availability,
  _numberOfRooms,
  multiRoom = false,
  maxRooms = 1,
  guestsTypes = null,
  blackoutPeriods = [],
  weekDaysBlocked = [],
  messaging = null,
  errorOnSearch = false,
  item = null
}) => {
  const getValidDateInterval = (dateInterval) => {
    const newDateInterval = [];
    if(dateInterval[0] < new Date()) newDateInterval.push(new Date())
    else newDateInterval.push(dateInterval[0]);
    newDateInterval.push(dateInterval[1])
    return newDateInterval;
  }
  const temporalTranslations = provisionalModuleDataBasedOnLanguage(selectedLanguage);
  const translationsGuestSelector = translations(selectedLanguage);

  const [dateRange, setDateRange] = React.useState([arrivalDate, departureDate]);
  const [_startDate, _endDate] = dateRange;
  const [rangeIntervalState, setRangeIntervalState] = React.useState(getValidDateInterval(rangeInterval))
  const [numberOfRooms, setNumberOfRooms] = React.useState(_numberOfRooms || 1);

  const [_guests, setGuests] = React.useState(guests && guests.length > 0 ? guests : [[{type: "adult", age:undefined}]]);

  useEffect(() => {
    if (arrivalDate !== dateRange[0] || departureDate !== dateRange[1]) {
      setDateRange([arrivalDate, departureDate]);
    }
    if (guests !== _guests && guests && guests.length) {
      setGuests(guests);
    }
    if (_numberOfRooms !== numberOfRooms && _numberOfRooms) {
      setNumberOfRooms(_numberOfRooms)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivalDate, departureDate, guests, _numberOfRooms])

  const updateDate = (dates) => {
    if(dates[0] && !dates[1]){
      if(maxStay || minStay){
        const minDate = minStay ? addDays(dates[0], minStay) : dates[0];
        const maxDate = maxStay ? addDays(dates[0], maxStay) : null;
        setRangeIntervalState([minDate, maxDate ])
      }
    }
    setDateRange(dates)
  }

  const onOpen = () => {
     //reset range interval once start and end date selected
     if(_startDate && _endDate) setRangeIntervalState(getValidDateInterval(rangeInterval))
  }

  const updateGuests = (index, guests) => {
    let newGuests = [..._guests];
    newGuests[index] = guests;
    setGuests(newGuests);
  }

  const formTitle = (type !== "activity" && type !== "restaurant" && type !== "activities" && type !== "restaurants" && type !== "lift-ticket") ? temporalTranslations.searchComing : temporalTranslations.formTitleActivities;

  const disabled = (!(_startDate && _endDate && _guests.length ) && (type === "lodging" || type === "hotel")) || (!_startDate && (type === "activity" || type === "restaurant" ));
  const types = guestsTypes ? [
    {
      type: "adult",
      label: translationsGuestSelector.adultsLabel,
      minRange: guestsTypes.min_adults,
      maxRange: guestsTypes.max_adults,
      minAge: guestsTypes.junior_max_age ? guestsTypes.junior_max_age + 1 : 13,
      maxAge: false
    },
    {
      type: "child",
      label: translationsGuestSelector.childrenLabel,
      minRange: guestsTypes.min_junior,
      maxRange: guestsTypes.max_junior,
      minAge: guestsTypes.junior_min_age ? guestsTypes.junior_min_age : 0,
      maxAge: guestsTypes.junior_max_age ? guestsTypes.junior_max_age : 13,
      ageRequired: true,
      ageRequiredLabel: translationsGuestSelector.ageRequiredLabel,
      ageRequiredCaption: translationsGuestSelector.ageRequiredCaption,
      ageLabel: translationsGuestSelector.ageLabel,
    },
  ] : null;

  return (
    <Modal
      open={open}
      title={temporalTranslations.searchTitle}
      size="small"
      alignedCenter
      footer={
        <button
          disabled={disabled}
          className={`${disabled ? "bg-gray-100" : "text-[color:var(--secondaryColor)] bg-[color:var(--primaryColor)]"} w-full text-white flex justify-between items-center gap-2 rounded-full p-4 font-manrope text-[0.9375rem] leading-[1.175rem] font-[800]`}
          onClick={() => onSearch(_startDate, _endDate, _guests, numberOfRooms)}
        >
          <Icon icon="ArrowRight" className="w-4 h-4 invisible" />
          <span className="buttonText block flex-1">{temporalTranslations.select}</span>
          <Icon icon="ArrowRight" className="w-4 h-4" />
        </button>
      }
      closeFn={closeFn}
    >
      <div className="min-h-[400px]">
        <span className="block font-[800] text-[0.9375rem] leading-[1.175rem] mb-4">{formTitle}</span>
        {
          type !== "activity" && type !== "restaurant" && type !== "activities" && type !== "restaurants" && type !== "lift-ticket" ?
          <DateRangePicker
            weekdaysBlocked={weekDaysBlocked.map(day => parseInt(day))}
            onOpen={onOpen}
            minDate={rangeIntervalState[0]}
            maxDate={rangeIntervalState[1]}
            startDatePlaceholder={temporalTranslations.startDatePlaceholder}
            endDatePlaceholder={temporalTranslations.endDatePlaceholder}
            startDate={_startDate}
            endDate={_endDate}
            onChange={updateDate}
            locale={selectedLanguage}
            shouldCloseOnSelect={true}
            onMonthChange={onMonthChange}
            availability={availability}
            monthsShown={1}
            excludeDateIntervals={blackoutPeriods && blackoutPeriods.length && blackoutPeriods.map(bp => { return { start: new Date(bp.From), end: new Date(bp.To) }})}
            item={item}
          />
          :
          <DatePicker
            locale={selectedLanguage}
            minDate={staticArrivalDateForActivities || new Date()}
            maxDate={departureDate}
            years={[new Date().getFullYear(), new Date().getFullYear() + 1]}
            value={_startDate}
            label={temporalTranslations.startDatePlaceholder}
            includeDateIntervals={null}
            weekdaysBlocked={weekDaysBlocked.map(day => parseInt(day))}
            onChange={(date) => {
              setDateRange([date, departureDate])
            }}
            item={item}
            excludeDateIntervals={blackoutPeriods && blackoutPeriods.length && blackoutPeriods.map(bp => { return { start: new Date(bp.From).setHours(0, 0, 0, 0), end: new Date(bp.To) }})}
          />
        }
        {/* Add informative messages below date picker */}
        {messaging?.map((msg, index) => {
          if (msg.search_form_messaging_id.type === "informative") {
            return (
              <div key={index} className="mt-4">
                {getLanguageField(msg.search_form_messaging_id.translations, selectedLanguage, "message")}
              </div>
            );
          }
          return null;
        })}
        {
          type !== "activity" && type !== "restaurant" && type !== "activities" && type !== "restaurants" && type !== "lift-ticket" ?
          <>
            <span className="block font-[800] text-[0.9375rem] leading-[1.175rem] mt-6 mb-4">{temporalTranslations.searchWho}</span>
            {
              Array.from({length: numberOfRooms }, (_, index) => {
                const isLast = index === numberOfRooms -1;
                return (
                  <>
                     {numberOfRooms > 1 && <div className="flex gap-2 items-center justify-between mb-4">
                        <span className="font-[700]">{temporalTranslations.room} {index + 1}</span>
                        {
                          isLast ?
                            <button className="flex border rounded-lg p-2"
                              onClick={() => {
                                setNumberOfRooms(numberOfRooms - 1);
                                const newGuests = [..._guests];
                                newGuests.splice(_guests.length - 1);
                                setGuests(newGuests);
                              }}
                            >
                              <Icon icon="X"/>
                            </button>
                          : null
                        }
                      </div>
                    }
                    <div key={`selector_${index}`} className="border-2 border[#EAEAF2] p-4 rounded-2xl mb-4">
                      <GuestSelector
                        types={types}
                        language={selectedLanguage}
                        accentColor={"#6D62E4"}
                        guests={_guests[index]}
                        setGuests={(guests) => updateGuests(index, guests)}
                      />
                    </div>
                  </>
                )
              })
            }
            {
              multiRoom && numberOfRooms <= maxRooms
              ? <button
                  onClick={() => {
                    setNumberOfRooms(numberOfRooms + 1);
                    const newGuests = [..._guests];
                    newGuests.push([{type: "adult", age: undefined}]);
                    setGuests(newGuests);
                  }}
                  className={`flex items-center gap-2 mt-4 font-[800] underline text-[color:var(--primaryColor)]`}
                >
                  <Icon icon="Plus" />
                  <span>{temporalTranslations.addRoom}</span>
                </button>
              : null
            }
          </>
          : null
        }
        {/* For error messages, add this where appropriate (usually near form validation or error states) */}
        {errorOnSearch ? messaging?.map((msg, index) => {
          if (msg.search_form_messaging_id.type === "error") {
            return (
              <div key={index} className="rounded-lg bg-[#FDEDED] text-[#E10003] p-4 font-[600]">
                {getLanguageField(msg.search_form_messaging_id.translations, selectedLanguage, "message")}
              </div>
            );
          }
          return null;
        }) : null}
        </div>
    </Modal>
  );
};

export default SearchModal;
